import { applyDelay } from "./utils/applyDelay.js";
import { BaseConversation, } from "./BaseConversation.js";
import { setupStrategy } from "./platform/VoiceSessionSetup.js";
export class VoiceConversation extends BaseConversation {
    input;
    output;
    playbackEventTarget;
    cleanUp;
    wakeLock;
    type = "voice";
    static async requestWakeLock() {
        if ("wakeLock" in navigator) {
            // unavailable without HTTPS, including localhost in dev
            try {
                return await navigator.wakeLock.request("screen");
            }
            catch (_e) {
                // Wake Lock is not required for the conversation to work
            }
        }
        return null;
    }
    static async startSession(options) {
        const fullOptions = BaseConversation.getFullOptions(options);
        if (fullOptions.onStatusChange) {
            fullOptions.onStatusChange({ status: "connecting" });
        }
        if (fullOptions.onCanSendFeedbackChange) {
            fullOptions.onCanSendFeedbackChange({ canSendFeedback: false });
        }
        let preliminaryInputStream = null;
        let conversation = null;
        const useWakeLock = options.useWakeLock ?? true;
        let wakeLock = null;
        if (useWakeLock) {
            wakeLock = await VoiceConversation.requestWakeLock();
        }
        try {
            // some browsers won't allow calling getSupportedConstraints or enumerateDevices
            // before getting approval for microphone access
            preliminaryInputStream = await navigator.mediaDevices.getUserMedia({
                audio: true,
            });
            await applyDelay(fullOptions.connectionDelay);
            // Platform-specific strategy creates the connection and sets up input/output
            const sessionSetup = await setupStrategy(fullOptions);
            // Stop the preliminary stream after setting up the session.
            // Its only purpose was triggering the browser's microphone permission
            // prompt; it must remain alive until the strategy finishes because
            // MediaDeviceInput.create (WebSocket path) needs mic access granted.
            preliminaryInputStream?.getTracks().forEach(track => {
                track.stop();
            });
            preliminaryInputStream = null;
            conversation = new VoiceConversation(fullOptions, sessionSetup.connection, sessionSetup.input, sessionSetup.output, sessionSetup.playbackEventTarget, sessionSetup.detach, wakeLock);
            fullOptions.onConversationCreated?.(conversation);
            conversation.markConnected();
            fullOptions.onConnect?.({
                conversationId: sessionSetup.connection.conversationId,
            });
            return conversation;
        }
        catch (error) {
            preliminaryInputStream?.getTracks().forEach(track => {
                track.stop();
            });
            if (conversation) {
                await conversation.endSession().catch(() => { });
            }
            else {
                fullOptions.onStatusChange?.({ status: "disconnected" });
            }
            try {
                await wakeLock?.release();
                wakeLock = null;
            }
            catch (_e) { }
            throw error;
        }
    }
    inputFrequencyData;
    outputFrequencyData;
    visibilityChangeHandler = null;
    handlePlaybackEvent = event => {
        if (event.data.type === "process") {
            this.updateMode(event.data.finished ? "listening" : "speaking");
        }
    };
    constructor(options, connection, input, output, playbackEventTarget, cleanUp, wakeLock) {
        super(options, connection);
        this.input = input;
        this.output = output;
        this.playbackEventTarget = playbackEventTarget;
        this.cleanUp = cleanUp;
        this.wakeLock = wakeLock;
        playbackEventTarget?.addListener(this.handlePlaybackEvent);
        if (wakeLock) {
            // Wake locks are automatically released when a page is hidden like when switching tabs
            // so attempt to re-acquire lock when page becomes visible again
            this.visibilityChangeHandler = () => {
                if (document.visibilityState === "visible" && this.wakeLock?.released) {
                    VoiceConversation.requestWakeLock().then(lock => {
                        this.wakeLock = lock;
                    });
                }
            };
            document.addEventListener("visibilitychange", this.visibilityChangeHandler);
        }
    }
    async handleEndSession() {
        this.cleanUp();
        this.playbackEventTarget?.removeListener(this.handlePlaybackEvent);
        this.playbackEventTarget = null;
        await super.handleEndSession();
        if (this.visibilityChangeHandler) {
            document.removeEventListener("visibilitychange", this.visibilityChangeHandler);
        }
        try {
            await this.wakeLock?.release();
            this.wakeLock = null;
        }
        catch (_e) { }
        await this.input.close();
        await this.output.close();
    }
    handleInterruption(event) {
        super.handleInterruption(event);
        this.updateMode("listening");
        this.output.interrupt();
    }
    handleAudio(event) {
        super.handleAudio(event);
        if (event.audio_event.alignment && this.options.onAudioAlignment) {
            this.options.onAudioAlignment(event.audio_event.alignment);
        }
        if (this.lastInterruptTimestamp <= event.audio_event.event_id) {
            if (event.audio_event.audio_base_64) {
                this.options.onAudio?.(event.audio_event.audio_base_64);
                // Audio routing is handled by attachConnectionToOutput for WebSocket
                // WebRTC handles audio playback directly through LiveKit tracks
            }
            this.currentEventId = event.audio_event.event_id;
            this.updateCanSendFeedback();
            this.updateMode("speaking");
        }
    }
    static FREQUENCY_BIN_COUNT = 1024;
    setMicMuted(isMuted) {
        this.input.setMuted(isMuted).catch(error => {
            this.options.onError?.("Failed to set input muted state", error);
        });
    }
    getInputByteFrequencyData() {
        this.inputFrequencyData ??= new Uint8Array(VoiceConversation.FREQUENCY_BIN_COUNT);
        this.input.getByteFrequencyData(this.inputFrequencyData);
        return this.inputFrequencyData;
    }
    getOutputByteFrequencyData() {
        this.outputFrequencyData ??= new Uint8Array(VoiceConversation.FREQUENCY_BIN_COUNT);
        this.output.getByteFrequencyData(this.outputFrequencyData);
        return this.outputFrequencyData;
    }
    getInputVolume() {
        return this.input.getVolume();
    }
    getOutputVolume() {
        return this.output.getVolume();
    }
    async changeInputDevice({ sampleRate, format, preferHeadphonesForIosDevices, inputDeviceId, }) {
        try {
            await this.input.setDevice({
                inputDeviceId,
                sampleRate,
                format,
                preferHeadphonesForIosDevices,
            });
        }
        catch (error) {
            console.error("Error changing input device", error);
            throw error;
        }
    }
    async changeOutputDevice({ sampleRate, format, outputDeviceId, }) {
        try {
            await this.output.setDevice({
                outputDeviceId,
                sampleRate,
                format,
            });
        }
        catch (error) {
            console.error("Error changing output device", error);
            throw error;
        }
    }
    setVolume = ({ volume }) => {
        // clamp & coerce
        const clampedVolume = Number.isFinite(volume)
            ? Math.min(1, Math.max(0, volume))
            : 1;
        this.volume = clampedVolume;
        // Delegate to output controller
        this.output.setVolume(clampedVolume);
    };
}
//# sourceMappingURL=VoiceConversation.js.map